import { useState } from 'react';
import { Box, Flex, HStack, Progress, SimpleGrid, Spinner, Text, Tooltip } from '@chakra-ui/react';

import { SectionCard, StatCard, EmptyState, ErrorState } from '../_ui/components';
import { useCampaignUI } from '../_ui/ui';
import { useCampaignStatsQuery } from 'api/metaWhatsappApi';
import { PillSelect } from '../_ui/controls';
import { cumulativeFunnel, pctOf } from './funnel';
import usePageVisibility from '../hooks/usePageVisibility';

/**
 * What actually happened — read from the recipient rows, not the counters.
 *
 * ★ THE ROWS ARE THE TRUTH; THE COUNTERS ARE A CACHE. `campaign.counters` are
 * incremented as events arrive, which makes them instant and makes them drift —
 * a webhook applied twice, a crash between two writes. The list screen shows
 * those because a hundred campaigns cannot each afford an aggregation; this
 * screen aggregates, and when the two disagree this one is right.
 *
 * ★ THE FUNNEL IS CUMULATIVE — see `funnel.js`. The API counts each contact in
 * the one state it is in now, so drawn per state a campaign that is going well
 * looks worse with every reply. Each stage here is "got at least this far",
 * with the drop between stages written down, because the drop is the number a
 * person is actually looking for.
 *
 * Late receipts and replies can update completed campaigns. Polling is paused
 * while the document is hidden and the lazy tab unmounts when it is not selected.
 */

/** The ways the funnel can be sliced, and what each one answers. */
const BREAKDOWNS = [
	{ key: 'variant', label: 'Message variant', help: 'Which wording earns more replies.' },
	{ key: 'number', label: 'Sender number', help: 'Whether one number is carrying more than its share.' },
	{ key: 'day', label: 'Day', help: 'How the send is spread across the schedule.' },
	{ key: 'country', label: 'Country', help: 'Where the audience actually is.' },
	{ key: 'language', label: 'Language', help: '' },
	{ key: 'source', label: 'Source', help: 'CRM audience versus uploaded list.' },
	{ key: 'category', label: 'Billing category', help: "Meta's own category, which is what it charged for." },
];

const num = (v) => Number(v ?? 0).toLocaleString();
const pct = (v) => `${Number(v ?? 0).toFixed(1)}%`;

/**
 * Money, to four decimals rather than two.
 *
 * ★ A SINGLE WHATSAPP MESSAGE COSTS LESS THAN A CENT IN MOST COUNTRIES. Rounded
 * to the usual two places, a day that sent four hundred utility messages reads
 * as "$0.00" — which looks like a bug, or worse, like it was free. The rate card
 * is stored at this precision for the same reason.
 */
const money = (v, currency) => `${Number(v ?? 0).toFixed(4)} ${currency ?? ''}`.trim();

/** The funnel drawn as one: bars narrow from top to bottom, and the drop between them is written. */
function Funnel({ stages }) {
	const ui = useCampaignUI();
	const top = stages[0]?.count ?? 0;
	if (!top) {
		return (
			<Text fontSize='sm' color={ui.textMuted}>
				Nothing has been attempted yet.
			</Text>
		);
	}
	return (
		<Box>
			{stages.map((s, i) => (
				<Box key={s.key}>
					{i > 0 && (
						<Flex justify='center' py={0.5} aria-hidden>
							<Text fontSize='xs' color={s.drop ? ui.textMuted : ui.borderStrong}>
								{s.drop ? `− ${num(s.drop)}` : '·'}
							</Text>
						</Flex>
					)}
					<Flex align='center' gap={3}>
						<Text w={{ base: '96px', md: '150px' }} fontSize='sm' color={ui.textSecondary} flexShrink={0} noOfLines={1}>
							{s.label}
						</Text>
						<Flex flex='1' justify='center' minW={0}>
							<Tooltip hasArrow label={`${s.label}: ${num(s.count)} — ${pctOf(s.share)} of attempted`}>
								<Box
									h='26px'
									w={`${Math.max(s.share * 100, s.count ? 3 : 0)}%`}
									minW={s.count ? '10px' : 0}
									borderRadius='md'
									bg={ui.accent}
									opacity={1 - i * 0.11}
									transition='width 300ms ease'
									role='img'
									aria-label={`${s.label} ${num(s.count)}`}
								/>
							</Tooltip>
						</Flex>
						<Box w={{ base: '64px', md: '110px' }} textAlign='right' flexShrink={0}>
							<Text fontSize='sm' color={ui.textPrimary} fontWeight='semibold' lineHeight='1.1'>
								{num(s.count)}
							</Text>
							<Text fontSize='xs' color={ui.textMuted}>
								{pctOf(s.share)}
							</Text>
						</Box>
					</Flex>
				</Box>
			))}
		</Box>
	);
}

export default function CampaignOverview({ campaign }) {
	const ui = useCampaignUI();
	const [by, setBy] = useState('variant');

	const visible = usePageVisibility();
	const { data, isLoading, isError, refetch } = useCampaignStatsQuery(
		// M4. The money comes back on the same dimension as the counts, so a row
		// can read "180 sent · $4.14" instead of making somebody hold two screens
		// side by side and trust that both were counted the same way.
		{ id: campaign._id, by, metric: 'cost' },
		// Late receipts may arrive after the send cycle completes.
		{ pollingInterval: visible ? 30000 : 0, refetchOnMountOrArgChange: true },
	);

	if (isLoading) {
		return (
			<Flex justify='center' py={12}>
				<Spinner color={ui.accentInk} />
			</Flex>
		);
	}

	if (isError || !data) {
		return <ErrorState title='We could not load the campaign figures' description='Retry to fetch the latest recipient statistics.' onRetry={refetch} />;
	}

	const funnel = data.funnel ?? {};
	const totals = data.totals ?? {};
	const cost = data.cost ?? {};
	// The cost split arrives on the same dimension as the counts, so it is a
	// lookup rather than a second list to reconcile by eye.
	const costByKey = new Map((data.costBy?.rows ?? []).map((r) => [r.key, r]));
	const attempted = totals.attempted ?? 0;
	const stages = cumulativeFunnel(funnel, attempted);
	const delivered = stages.find((s) => s.key === 'delivered')?.count ?? 0;

	const aside = [
		{ label: 'Waiting', value: funnel.SCHEDULED, tone: ui.textSecondary, tip: 'Built and due, not yet sent.' },
		{
			label: 'Not delivered',
			value: data.undelivered,
			tone: ui.warning,
			tip: 'Sent and charged for, but never confirmed delivered after 72 hours. WhatsApp never says whether someone blocked you or was simply offline, so this is not a failure.',
		},
		{ label: 'Failed', value: funnel.FAILED, tone: ui.danger, tip: 'WhatsApp refused the send. The reason is on each row under Recipients.' },
		{ label: 'Opted out', value: funnel.OPTED_OUT, tone: ui.warning, tip: 'Replied STOP. They are on the do-not-message list now.' },
		{ label: 'Skipped by a safety rule', value: funnel.SKIPPED, tone: ui.textSecondary, tip: 'Consent, frequency cap, or the do-not-message list.' },
		{ label: 'Proven on WhatsApp', value: data.provenValid, tone: ui.success, tip: 'This campaign proved these numbers are on WhatsApp — every future audience benefits.' },
	];

	return (
		<Box>
			<SimpleGrid columns={{ base: 2, xl: 4 }} spacing={4} mb={5}>
				<StatCard label='Attempted' value={num(attempted)} help={`${num(funnel.FAILED ?? 0)} failed`} />
				<StatCard label='Delivered' value={pct(totals.deliveredPct)} help={`${num(delivered)} people`} />
				<StatCard label='Replied' value={pct(totals.repliedPct)} help={`${num(funnel.REPLIED ?? 0)} conversations`} />
				<StatCard
					label='Cost'
					// The `+` is not decoration: it marks a total that is known to be
					// short. Without it a figure missing four hundred unpriced
					// messages looks exactly like a complete one.
					value={cost.rated ? `${money(cost.amount, cost.currency)}${cost.unpriced ? '+' : ''}` : '—'}
					// ★ An empty rate card reports "unpriced" rather than guessing.
					// A number invented here would be quoted to a client.
					help={
						// eslint-disable-next-line no-nested-ternary
						!cost.rated
							? 'No rate card set'
							: cost.unpriced
								? `${num(cost.priced)} billable · ${num(cost.unpriced)} with no rate`
								: `${num(cost.priced)} billable`
					}
				/>
			</SimpleGrid>

			<SectionCard title='The funnel' subtitle='How far each contact got. Each bar is everyone who reached at least that stage.' mb={5}>
				<Funnel stages={stages} />

				<SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3} mt={6} pt={4} borderTopWidth='1px' borderColor={ui.borderSubtle}>
					{aside.map((a) => (
						<Tooltip key={a.label} hasArrow label={a.tip}>
							<Box>
								<Text fontSize='xs' color={ui.textMuted} noOfLines={1}>
									{a.label}
								</Text>
								<Text fontSize='lg' color={a.tone} fontWeight='semibold'>
									{num(a.value)}
								</Text>
							</Box>
						</Tooltip>
					))}
				</SimpleGrid>
			</SectionCard>

			<SectionCard title='Breakdown' subtitle={BREAKDOWNS.find((b) => b.key === by)?.help || 'Slice the funnel by any dimension.'} mb={5}>
				<Box mb={4}>
					<PillSelect label='Breakdown' value={by} onChange={setBy} options={BREAKDOWNS.map((b) => ({ value: b.key, label: b.label }))} />
				</Box>
				{!data.breakdown?.length ? (
					<Text fontSize='sm' color={ui.textMuted}>
						Nothing to break down yet.
					</Text>
				) : (
					data.breakdown.map((row) => {
						const total = row.total ?? 0;
						const replied = row.REPLIED ?? 0;
						const rowDelivered = (row.DELIVERED ?? 0) + (row.READ ?? 0) + replied;
						const share = total ? Math.round((rowDelivered / total) * 100) : 0;
						// M4. Keyed on the same dimension, so this is this row's money.
						const spend = costByKey.get(row.key);
						return (
							<Box key={row.key} mb={4}>
								<Flex justify='space-between' mb={1} gap={3}>
									<Text fontSize='sm' color={ui.textPrimary} noOfLines={1}>
										{row.key}
									</Text>
									<HStack spacing={2} flexShrink={0}>
										<Text fontSize='sm' color={ui.textMuted}>
											{num(total)} sent · {num(replied)} replied
										</Text>
										{spend && (
											<Tooltip
												hasArrow
												label={
													spend.unpriced
														? `${num(spend.unpriced)} message${spend.unpriced === 1 ? '' : 's'} here have no rate-card entry, so they are not in this figure.`
														: `${num(spend.priced)} billable message${spend.priced === 1 ? '' : 's'}`
												}
											>
												<Text fontSize='sm' color={spend.unpriced ? ui.warning : ui.textSecondary} fontWeight='medium'>
													{money(spend.amount, data.costBy?.currency)}
													{spend.unpriced ? '+' : ''}
												</Text>
											</Tooltip>
										)}
									</HStack>
								</Flex>
								<Progress value={share} size='xs' borderRadius='full' bg={ui.surfaceSubtle} colorScheme='brand' />
							</Box>
						);
					})
				)}
			</SectionCard>

			{campaign.plan?.days?.length ? (
				<SectionCard title='The day plan' subtitle='How the planner spread this audience, and across which zones.' mb={5}>
					{campaign.plan.days.map((day) => (
						<Flex key={day.day} justify='space-between' py={2} borderBottomWidth='1px' borderColor={ui.borderSubtle}>
							<Text fontSize='sm' color={ui.textPrimary}>
								{day.day}
							</Text>
							<Text fontSize='sm' color={ui.textMuted}>
								{num(day.count)} people
								{day.byZone ? ` · ${Object.keys(day.byZone).length} zone(s)` : ''}
							</Text>
						</Flex>
					))}
					{campaign.plan.tzFallbackCount > 0 && (
						<Text fontSize='xs' color={ui.warning} mt={3}>
							{num(campaign.plan.tzFallbackCount)} contacts had no time zone of their own and used the
							campaign default. Their local hour is a guess.
						</Text>
					)}
				</SectionCard>
			) : null}

			{/* ★ WHY THE SPLIT MOVED, NOT JUST THAT IT DID. The engine stores the
			    score each message was judged on precisely so this is reviewable;
			    showing only the new weights would leave an operator looking at a
			    split they did not set, with no way to tell whether it was
			    justified. The score is a LOWER BOUND on the reply rate, which is
			    why a message with a flattering raw average can still rank below
			    one with more evidence behind it. */}
			{campaign.variantOptimisation?.lastRunAt ? (
				<SectionCard title='Automatic split' subtitle='This campaign is favouring whichever message gets more replies.'>
					{(campaign.variants ?? []).map((variant) => {
						const score = campaign.variantOptimisation?.lastScores?.[variant.id];
						return (
							<Flex key={variant.id} justify='space-between' py={2} borderBottomWidth='1px' borderColor={ui.borderSubtle}>
								<Text fontSize='sm' color={ui.textPrimary}>
									Variant {String(variant.id).toUpperCase()}
								</Text>
								<Text fontSize='sm' color={ui.textMuted}>
									weight {num(variant.weight ?? 1)}
									{typeof score === 'number' ? ` · confident reply rate ${(score * 100).toFixed(1)}%` : ''}
								</Text>
							</Flex>
						);
					})}
					<Text fontSize='xs' color={ui.textMuted} mt={3} maxW='72ch'>
						Last reviewed {new Date(campaign.variantOptimisation.lastRunAt).toLocaleString()}. The percentage is
						the lowest the true reply rate is likely to be, not the raw average — a version with one reply from
						two sends scores below one with forty from two hundred, which is what stops an early run of luck
						deciding the campaign.
					</Text>
				</SectionCard>
			) : null}
		</Box>
	);
}
